// The practice's appointment statuses, for every component that used to keep
// its own hardcoded copy.
//
// Before this hook the same nine statuses were declared in four separate arrays
// plus a ternary chain, and they had already drifted — the same status carried
// different icon colours in different screens. Everything now reads from here.
//
// Two rules the consumers depend on:
//
//   * **Fail open.** `resolve()` always returns something renderable. A status
//     that is not configured — an import, a legacy value, one archived away —
//     must still show up with a colour and an icon. Three separate screens used
//     to fail closed, hiding an appointment or rendering no icon at all.
//   * **Multi-practice aware.** The calendar merges appointments from every
//     practice the user belongs to, and statuses are configured per practice.
//     Lookups therefore take the appointment's own practice; the merged list is
//     only for UI that needs one set of options, like the filter panel.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAppointmentStatuses } from "../api";

// Rendered for a status we cannot resolve at all. Mirrors FALLBACK_STATUS in
// backend/src/services/appointmentStatusService.js.
const FALLBACK = {
  key: "",
  label: "Unknown",
  type: "General",
  color: "#6c757d",
  icon: "fa-circle-question",
  showByDefault: true,
  archived: false,
};

const FALLBACK_BEHAVIOUR = {
  blocksSlot: true,
  sendReminders: true,
  attendance: "booked",
  terminal: false,
  blocksRow: false,
  strikeThrough: false,
  allowsInvoicing: true,
  selectable: true,
};

const norm = (v) => String(v ?? "").trim();

/**
 * @param {string[]|string} practiceNumbers practices to load statuses for
 */
export function useAppointmentStatuses(practiceNumbers) {
  const [byPractice, setByPractice] = useState({});
  const [typeBehaviour, setTypeBehaviour] = useState({});
  const [loading, setLoading] = useState(true);

  // Stable key so the effect doesn't refire on a new array with the same
  // contents — which would otherwise refetch on every parent render.
  const list = useMemo(
    () => [].concat(practiceNumbers || []).map(String).filter(Boolean).sort(),
    [practiceNumbers]
  );
  const listKey = list.join(",");

  useEffect(() => {
    if (!listKey) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    (async () => {
      const entries = await Promise.all(
        listKey.split(",").map(async (pn) => {
          try {
            const data = await getAppointmentStatuses(pn);
            return [pn, data];
          } catch {
            // One practice failing must not blank the whole calendar.
            return [pn, null];
          }
        })
      );
      if (cancelled) return;

      const next = {};
      let behaviour = null;
      for (const [pn, data] of entries) {
        if (!data) continue;
        next[pn] = Array.isArray(data.statuses) ? data.statuses : [];
        if (!behaviour && data.typeBehaviour) behaviour = data.typeBehaviour;
      }
      setByPractice(next);
      if (behaviour) setTypeBehaviour(behaviour);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [listKey]);

  // One deduped list for UI that shows a single set of options. Keyed by
  // `key`, so two practices sharing a seeded status contribute one entry.
  const statuses = useMemo(() => {
    const seen = new Map();
    for (const pn of Object.keys(byPractice)) {
      for (const s of byPractice[pn] || []) {
        if (!s?.key || s.archived) continue;
        if (!seen.has(s.key)) seen.set(s.key, s);
      }
    }
    return [...seen.values()];
  }, [byPractice]);

  /**
   * The live statuses of ONE practice — what a picker must offer.
   *
   * `statuses` above merges every practice the user belongs to, which is right
   * for the calendar filter panel but wrong for a picker: it would show
   * practice A's renamed label and colour while the appointment belongs to
   * practice B, so the label the user picked and the colour the appointment
   * then rendered in came from two different configs.
   *
   * Falls back to the merged list when the practice has no entry, so a modal
   * that opens before its appointment's practice is known still offers
   * something.
   */
  const statusesFor = useCallback(
    (practiceNumber) => {
      const scoped = practiceNumber ? byPractice[String(practiceNumber)] : null;
      if (!scoped?.length) return statuses;
      return scoped.filter((s) => s?.key && !s.archived);
    },
    [byPractice, statuses]
  );

  const byKey = useMemo(() => {
    const m = new Map();
    for (const s of statuses) m.set(norm(s.key), s);
    return m;
  }, [statuses]);

  /** Resolve a status. Never returns null — see the fail-open rule above. */
  const resolve = useCallback(
    (key, practiceNumber) => {
      const wanted = norm(key);
      const scoped = practiceNumber ? byPractice[String(practiceNumber)] : null;
      const hit =
        (scoped || []).find((s) => norm(s.key) === wanted) || byKey.get(wanted);
      if (hit) return hit;
      return { ...FALLBACK, key: wanted, label: wanted || FALLBACK.label };
    },
    [byPractice, byKey]
  );

  const typeOf = useCallback(
    (key, practiceNumber) => resolve(key, practiceNumber).type || FALLBACK.type,
    [resolve]
  );

  const behaviourOf = useCallback(
    (key, practiceNumber) =>
      typeBehaviour[typeOf(key, practiceNumber)] || FALLBACK_BEHAVIOUR,
    [typeBehaviour, typeOf]
  );

  const colorFor = useCallback(
    (key, practiceNumber) => resolve(key, practiceNumber).color || FALLBACK.color,
    [resolve]
  );

  const iconFor = useCallback(
    (key, practiceNumber) => resolve(key, practiceNumber).icon || FALLBACK.icon,
    [resolve]
  );

  const labelFor = useCallback(
    (key, practiceNumber) => resolve(key, practiceNumber).label || norm(key),
    [resolve]
  );

  /**
   * The status to WRITE when the app needs one of a given type — the first live
   * status of that type, in the practice's own order. Mirrors
   * `defaultStatusForType` on the backend so a renamed status never produces an
   * orphan value.
   */
  const statusKeyForType = useCallback(
    (type, practiceNumber) => {
      const scoped = practiceNumber ? byPractice[String(practiceNumber)] : null;
      const pool = scoped?.length ? scoped : statuses;
      const hit = pool.find((s) => s?.type === type && !s?.archived);
      return hit?.key || "";
    },
    [byPractice, statuses]
  );

  /** Statuses ticked on when the calendar first loads. */
  const defaultVisibleKeys = useMemo(
    () => statuses.filter((s) => s.showByDefault).map((s) => s.key),
    [statuses]
  );

  /** Statuses offered in a picker — Scheduling is generated, never chosen. */
  const selectableStatuses = useMemo(
    () => statuses.filter((s) => (typeBehaviour[s.type] || FALLBACK_BEHAVIOUR).selectable),
    [statuses, typeBehaviour]
  );

  return {
    loading,
    statuses,
    statusesFor,
    selectableStatuses,
    byKey,
    resolve,
    typeOf,
    behaviourOf,
    colorFor,
    iconFor,
    labelFor,
    statusKeyForType,
    defaultVisibleKeys,
  };
}

// Types a new-patient appointment may move to. It cannot go straight to
// completed — the server enforces that with a 409 — so the picker greys the
// rest out rather than letting someone hit an error.
const NEW_PATIENT_NEXT = ["NewPatient", "Cancelled", "NoShow"];

/**
 * Can an appointment move from one status to another?
 *
 * Reproduces the rule table that used to be duplicated verbatim in
 * UpdateStatus.jsx and EditAppointments.jsx, but keyed on type so a practice's
 * own statuses inherit the same rules.
 */
export function isTransitionAllowed(fromType, toType, behaviourFor) {
  const to = behaviourFor(toType);
  const from = behaviourFor(fromType);

  // Availability blocks are generated, never chosen.
  if (to.selectable === false) return false;
  // Terminal states are the end of the line.
  if (from.terminal) return false;
  if (fromType === "NewPatient" && !NEW_PATIENT_NEXT.includes(toType)) return false;
  return true;
}

/**
 * Seed a filter selection once, without stomping the user's choices.
 *
 * The old effect reset `selectedStatuses` whenever its `statuses` dependency
 * changed identity. That was harmless while the list was a `useMemo(…, [])`,
 * but now that it arrives from a fetch the same effect would wipe whatever the
 * user had ticked the moment the request resolved.
 */
export function useSeedOnce(ready, seed, apply) {
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !ready) return;
    done.current = true;
    apply(seed);
    // `apply` and `seed` are read only on the one run this performs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);
}

export default useAppointmentStatuses;
